// The private notes of the file a diff view shows (docs/design.md "Notes"):
// it asks the repository's notes store for the notes on the file's blobs
// (those shown, and the file's blobs at the track's other commits), places
// them on the lines shown (`review/notes.ts`), asks for the line maps that
// carry notes from other blobs, and holds the note being written.
//
// A view makes one, during its setup, and reads `at` where it draws the
// rows under a line. The view's own state stays its own: this takes what
// it needs as a function of it (`input`).
import { EMPTY_TREE, type Track } from '../logic/track';
import type { FileSides } from '../engine/types';
import { placeNotes, type NotePlacement, type PlacedNote } from '../review/notes';
import { anchorKey } from './anchor';
import { clearDraft } from './drafts';
import type { NotesStore } from './notes.svelte';

/** What the file notes read of the view, each time it changes. */
export interface FileNotesInput {
  /** The open file's path, or null for none. */
  path: string | null;
  /** Its sides' paths and blobs, once its diff is ready. */
  sides: FileSides | null;
  /** Former paths of the file (a rename the comparison records). */
  renamedFrom: readonly string[];
  /** The points of the track the view shows (`trackPoints`): the file's blobs there are asked for too. */
  points: readonly string[];
}

export interface FileNotesDeps {
  /** The repository's notes. */
  store(): NotesStore;
  /** The line maps computed so far, by `lineMapKey(from, to)` (`DiffModel.lineMaps`). */
  lineMaps(): ReadonlyMap<string, readonly number[]>;
  /** The blobs of `paths` (and of their other names on the track) at `points`, by the name each was seen at (`state/trackBlobs.ts`). */
  blobsOnTrack(points: readonly string[], paths: readonly string[]): Promise<Map<string, string[]>>;
  /** Compute these line maps, and add them to `lineMaps` (`DiffModel.wantLineMaps`). */
  wantLineMaps(needs: readonly { from: string; to: string }[]): void;
  /** Another ID of a side's text, where its blob is GitHub's SHA and its decoded text's `blobId` differs (`DiffModel.aliasOf`): notes written on it are on the side. */
  aliasOf?(blob: string): string | undefined;
}

/** A note being written: on lines `from..to` of one side of the open file, written on the commit `commit` resolves to. */
export interface NoteDraft {
  /** The open file's path when it was started. */
  file: string;
  /** The side's path (a renamed file's old side has its old one). */
  path: string;
  side: 'LEFT' | 'RIGHT';
  blob: string;
  from: number;
  to: number;
  commit: () => Promise<string | null>;
  /** Text to start from: what a save that failed sent. */
  text?: string;
  /** Why the last save failed. */
  failed?: string;
}

/** The box a note being written keeps its text in (state/drafts.ts), so a reload keeps it. */
export function noteBox(d: Pick<NoteDraft, 'file' | 'side' | 'from' | 'to'>): string {
  return `note:${d.file}:${d.side}:${d.from}-${d.to}`;
}

/**
 * The points of `track` whose blobs a view asks about, in an order where a
 * commit comes after its parent when that is on the track: where its
 * history starts (`start`, the merge base of the base and the head, once
 * known; until then the base), the main lane oldest first, then the
 * commits force-pushes replaced (theirs are asked for directly when their
 * parent is not on the track). The empty tree has no blobs to ask about.
 */
export function trackPoints(track: Track, start: string | null): string[] {
  const out = new Set<string>();
  const first = start ?? track.base.sha;
  if (first && first !== EMPTY_TREE) out.add(first);
  for (const p of track.main) out.add(p.sha);
  for (const r of [...track.base.replaced, ...track.main.flatMap((p) => p.replaced), ...track.unheld.flatMap((u) => u.replaced)]) out.add(r.sha);
  return [...out];
}

/**
 * How many notes each block holds, by block key: the notes under each line
 * (`FileNotes.at`), counted for the block that line is in (`blockOf`, none
 * for a line no block draws). A block's head shows the count, so a folded
 * block (a change marked reviewed folds) still says it holds notes.
 */
export function notesByBlock(at: ReadonlyMap<string, readonly PlacedNote[]>, blockOf: (key: string) => string | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const [key, notes] of at) {
    const block = blockOf(key);
    if (block !== undefined) out.set(block, (out.get(block) ?? 0) + notes.length);
  }
  return out;
}

export class FileNotes {
  /** Where the open file's notes go. */
  readonly placement: NotePlacement;
  /** The notes shown on lines, by the key of the line they go under (`anchorKey`): their last. */
  readonly at: ReadonlyMap<string, PlacedNote[]>;
  /** The note being written, or null. */
  draft: NoteDraft | null = $state.raw(null);
  /** The key of the line the note being written goes under, while it is on the open file. */
  readonly draftKey: string | null;
  /**
   * The open file's blobs on the track, by every name it had there (renames
   * before the range shown too), as `blobsOnTrack` found them. With the
   * blobs shown, these are the only (path, blob) pairs whose notes the view
   * places: the repository's store holds other views' notes too, on other
   * versions of the same path.
   */
  private found: { path: string; blobs: ReadonlyMap<string, readonly string[]> } | null = $state.raw(null);
  private readonly input: () => FileNotesInput;
  private readonly deps: FileNotesDeps;

  constructor(input: () => FileNotesInput, deps: FileNotesDeps) {
    this.input = input;
    this.deps = deps;
    this.placement = $derived.by(() => {
      const i = this.input();
      if (!i.path || !i.sides) return { placed: [], lost: [], waiting: [] };
      const found = this.found?.path === i.path ? this.found.blobs : new Map<string, readonly string[]>();
      const pairs = new Set<string>();
      for (const s of [i.sides.old, i.sides.new]) {
        if (!s) continue;
        pairs.add(`${s.path}\0${s.blob}`);
        const alias = this.deps.aliasOf?.(s.blob);
        if (alias) pairs.add(`${s.path}\0${alias}`);
      }
      for (const [name, blobs] of found) for (const b of blobs) pairs.add(`${name}\0${b}`);
      const mine = this.deps.store().notes.filter((n) => pairs.has(`${n.path}\0${n.blob}`));
      const others = [...found.keys()].filter((n) => n !== i.path);
      const side = (s: FileSides['old']) => {
        if (!s) return null;
        const alias = this.deps.aliasOf?.(s.blob);
        return { ...s, renamedFrom: [...new Set([...i.renamedFrom, ...others])], ...(alias ? { aliases: [alias] } : {}) };
      };
      return placeNotes(mine, { old: side(i.sides.old), new: side(i.sides.new) }, this.deps.lineMaps());
    });
    this.at = $derived.by(() => {
      const out = new Map<string, PlacedNote[]>();
      for (const p of this.placement.placed) {
        const k = anchorKey(p.side === 'old' ? 'LEFT' : 'RIGHT', p.to);
        const list = out.get(k);
        if (list) list.push(p);
        else out.set(k, [p]);
      }
      return out;
    });
    this.draftKey = $derived(this.draft && this.draft.file === this.input().path ? anchorKey(this.draft.side, this.draft.to) : null);
    // The blobs shown, at once; the file's other blobs on the track once GitHub names them.
    $effect(() => {
      const { path, sides, renamedFrom, points } = this.input();
      if (!path || !sides) return;
      const store = this.deps.store();
      const shown = new Map<string, Set<string>>();
      for (const s of [sides.old, sides.new]) {
        if (!s) continue;
        const blobs = shown.get(s.path) ?? shown.set(s.path, new Set()).get(s.path)!;
        blobs.add(s.blob);
        const alias = this.deps.aliasOf?.(s.blob);
        if (alias) blobs.add(alias);
      }
      void store.want([...shown]);
      const paths = [...new Set([path, ...renamedFrom, ...shown.keys()])];
      let stale = false;
      void this.deps.blobsOnTrack(points, paths).then((found) => {
        if (stale) return;
        this.found = { path, blobs: found };
        void store.want([...found]);
      });
      return () => {
        stale = true;
      };
    });
    $effect(() => {
      const waiting = this.placement.waiting;
      if (waiting.length) this.deps.wantLineMaps(waiting);
    });
  }

  /**
   * Start a note on lines `a..b` of `side` of the open file, written on
   * the commit `commit` resolves to. False when the file's diff is not
   * ready (its blob is not known).
   */
  compose(side: 'LEFT' | 'RIGHT', a: number, b: number, commit: () => Promise<string | null>): boolean {
    const { path, sides } = this.input();
    const at = sides?.[side === 'LEFT' ? 'old' : 'new'];
    if (!path || !at) return false;
    this.draft = { file: path, path: at.path, side, blob: at.blob, from: Math.min(a, b), to: Math.max(a, b), commit };
    return true;
  }

  /** Close the note being written (its box keeps what was typed). */
  close() {
    this.draft = null;
  }

  /**
   * Save the note being written with `text`. It closes at once and the
   * note shows, being saved; when the server refuses it, it opens again
   * with the text and why. Its box is emptied only once it is saved.
   */
  async save(text: string): Promise<void> {
    const d = this.draft;
    if (!d || !text.trim()) return;
    this.draft = null;
    const fail = (why: string) => {
      // Unless another note was started meanwhile.
      if (!this.draft) this.draft = { ...d, text, failed: why };
    };
    const sha = await d.commit();
    if (!sha) return fail('the commit these lines are on is not known yet');
    try {
      await this.deps.store().create({
        path: d.path,
        side: d.side === 'LEFT' ? 'old' : 'new',
        blob: d.blob,
        start_line: d.from,
        end_line: d.to,
        commit_sha: sha,
        body: text,
      });
    } catch (e) {
      return fail(e instanceof Error ? e.message : String(e));
    }
    clearDraft(noteBox(d));
  }
}
